
import { supabase } from "@/integrations/supabase/client";
import { Business, BusinessCategory, BusinessListingFilters } from "@/types";

// Helper function to validate URL
const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch (e) {
    console.error(`Invalid URL format: ${url}`);
    return false;
  }
};

// Helper to sanitize URL, handling encoding issues
const sanitizeUrl = (url: string): string => {
  try {
    // Replace spaces and ensure proper encoding
    return encodeURI(decodeURI(url));
  } catch (e) {
    console.error(`Failed to sanitize URL: ${url}`, e);
    return url; // Return original if sanitization fails
  }
};

export const fetchBusinesses = async () => {
  try {
    console.log("Fetching businesses...");
    
    let query = supabase
      .from('businesses')
      .select('*')
      .eq('approved', true)  // Only fetch approved businesses
      .order('created_at', { ascending: false });
    
    const { data, error } = await query;
    
    if (error) {
      console.error("Error fetching businesses:", error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      console.log("No businesses found");
      return [];
    }
    
    // Process the data to ensure image fields are properly set
    const processedData = data.map(business => {
      // Filter out invalid URLs from media_urls
      const validMediaUrls = Array.isArray(business.media_urls) 
        ? business.media_urls
            .filter(url => typeof url === 'string' && url.trim() !== '')
            .map(sanitizeUrl)
            .filter(url => isValidUrl(url))
        : [];
      
      console.log(`Business ${business.name} has ${validMediaUrls.length} valid media URLs`);
      
      // Process image_url if it exists
      let imageUrl = business.image_url;
      if (imageUrl && typeof imageUrl === 'string' && imageUrl.trim() !== '') {
        imageUrl = sanitizeUrl(imageUrl);
        if (!isValidUrl(imageUrl)) {
          console.log(`Invalid image_url for ${business.name}: ${imageUrl}`);
          imageUrl = null;
        }
      } else {
        imageUrl = null;
      }
      
      // Ensure business has the expected structure
      const processedBusiness = {
        ...business,
        // Set media_urls to validated array
        media_urls: validMediaUrls,
        // Ensure imageUrl is set from either image_url or first media_url
        imageUrl: imageUrl || (validMediaUrls.length > 0 ? validMediaUrls[0] : '/images/business-placeholder.svg')
      };
      
      return processedBusiness;
    });
    
    console.log(`Fetched ${processedData.length} businesses successfully`);
    return processedData as Business[];
  } catch (error) {
    console.error("Error fetching businesses:", error);
    throw error;
  }
};

// For fetchBusinessById and fetchBusinessesByCategory, use the same process to validate URLs
export const fetchBusinessById = async (id: string) => {
  try {
    console.log(`Fetching business with ID: ${id}`);
    
    const { data, error } = await supabase
      .from('businesses')
      .select('*')
      .eq('id', id)
      .eq('approved', true)  // Only fetch if approved
      .maybeSingle();
    
    if (error) {
      console.error(`Error fetching business with ID ${id}:`, error);
      throw error;
    }
    
    if (!data) {
      console.log(`No business found with ID ${id}`);
      return null;
    }
    
    // Filter out invalid URLs from media_urls
    const validMediaUrls = Array.isArray(data.media_urls) 
      ? data.media_urls
          .filter(url => typeof url === 'string' && url.trim() !== '')
          .map(sanitizeUrl)
          .filter(url => isValidUrl(url))
      : [];
    
    // Process image_url if it exists
    let imageUrl = data.image_url;
    if (imageUrl && typeof imageUrl === 'string' && imageUrl.trim() !== '') {
      imageUrl = sanitizeUrl(imageUrl);
      if (!isValidUrl(imageUrl)) {
        console.log(`Invalid image_url for ${data.name}: ${imageUrl}`);
        imageUrl = null;
      }
    } else {
      imageUrl = null;
    }
    
    // Ensure the business has proper image URLs
    const processedBusiness = {
      ...data,
      // Set media_urls to validated array
      media_urls: validMediaUrls,
      // Ensure imageUrl is set from either image_url or first media_url
      imageUrl: imageUrl || (validMediaUrls.length > 0 ? validMediaUrls[0] : '/images/business-placeholder.svg')
    };
    
    console.log(`Successfully fetched business: ${processedBusiness.name}`);
    console.log(`Business has ${validMediaUrls.length} valid media URLs`);
    return processedBusiness as Business;
  } catch (error) {
    console.error(`Error fetching business with ID ${id}:`, error);
    throw error;
  }
};

export const fetchBusinessesByCategory = async (category: BusinessCategory) => {
  try {
    console.log(`Fetching businesses in category: ${category}`);
    
    const { data, error } = await supabase
      .from('businesses')
      .select('*')
      .eq('category', category)
      .eq('approved', true)  // Only fetch approved businesses
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error(`Error fetching businesses in category ${category}:`, error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      console.log(`No businesses found in category ${category}`);
      return [];
    }
    
    // Process and validate image URLs
    const processedData = data.map(business => {
      // Filter out invalid URLs from media_urls
      const validMediaUrls = Array.isArray(business.media_urls) 
        ? business.media_urls.filter(url => typeof url === 'string' && isValidUrl(url))
        : [];
      
      return {
        ...business,
        media_urls: validMediaUrls,
        imageUrl: (business.image_url && isValidUrl(business.image_url)) ? business.image_url : 
                 (validMediaUrls.length > 0 ? validMediaUrls[0] : '/images/business-placeholder.svg')
      };
    });
    
    console.log(`Fetched ${processedData.length} businesses in category ${category}`);
    return processedData as Business[];
  } catch (error) {
    console.error(`Error fetching businesses in category ${category}:`, error);
    throw error;
  }
};

export const searchBusinesses = async (filters: BusinessListingFilters) => {
  try {
    console.log("Searching businesses with filters:", filters);
    
    let query = supabase
      .from('businesses')
      .select('*')
      .eq('approved', true)  // Only fetch approved businesses
      .order('created_at', { ascending: false });
    
    if (filters.searchTerm) {
      query = query.or(`name.ilike.%${filters.searchTerm}%,description.ilike.%${filters.searchTerm}%`);
    }
    
    if (filters.category) {
      query = query.eq('category', filters.category);
    }
    
    if (filters.city) {
      query = query.eq('city', filters.city);
    }
    
    if (filters.tags && filters.tags.length > 0) {
      // This assumes tags is stored as an array in Supabase
      filters.tags.forEach(tag => {
        query = query.contains('tags', [tag]);
      });
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error("Error searching businesses:", error);
      throw error;
    }
    
    // Process and validate image URLs
    const processedData = data ? data.map(business => {
      // Filter out invalid URLs from media_urls
      const validMediaUrls = Array.isArray(business.media_urls) 
        ? business.media_urls.filter(url => typeof url === 'string' && isValidUrl(url))
        : [];
      
      return {
        ...business,
        media_urls: validMediaUrls,
        imageUrl: (business.image_url && isValidUrl(business.image_url)) ? business.image_url : 
                 (validMediaUrls.length > 0 ? validMediaUrls[0] : '/images/business-placeholder.svg')
      };
    }) : [];
    
    console.log(`Search returned ${processedData.length} results`);
    return processedData;
  } catch (error) {
    console.error("Error searching businesses:", error);
    throw error;
  }
};
